import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { IVideoGeneration, VideoGenerationStatus, IUserModel, PaginationQuery, VideoGenerationQuery, VideoGenerationType, IVideoLipsync, VideoLipsyncStatus, VideoLipsyncQuery } from '@packages/global';
import { videoGenerationApi } from '../../services/user/videoGeneration.api';
import { videoLipsyncApi } from '../../services/user/videoLipsync.api';
import Pagination from '../Pagination';

type TabType = 'all' | 'text2video' | 'image2video' | 'lipsync';

interface VideoSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (video: IVideoGeneration) => void;
  models: IUserModel[];
}

export default function VideoSelector({ isOpen, onClose, onSelect, models }: VideoSelectorProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [videos, setVideos] = useState<IVideoGeneration[]>([]);
  const [lipsyncVideos, setLipsyncVideos] = useState<IVideoLipsync[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<IVideoGeneration | null>(null);
  const [selectedLipsync, setSelectedLipsync] = useState<IVideoLipsync | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, page: 1, limit: 12 });

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const loadVideos = async (page: number = 1) => {
    setLoading(true);
    try {
      if (activeTab === 'lipsync') {
        const query: VideoLipsyncQuery = {
          search: search || undefined,
          status: VideoLipsyncStatus.COMPLETED
        };
        const queryData: PaginationQuery<VideoLipsyncQuery> = {
          query,
          pagination: { page, limit: 16, sortBy: 'createdAt', sortOrder: 'desc' }
        };
        const response = await videoLipsyncApi.videoLipsyncPageList(queryData);
        setLipsyncVideos(response.items);
        setPagination(response.pagination);
      } else {
        let query: VideoGenerationQuery = {
          search: search || undefined,
          status: VideoGenerationStatus.COMPLETED
        }
        if (activeTab === 'text2video') {
          query.type = VideoGenerationType.TEXT_TO_VIDEO;
        } else if (activeTab === 'image2video') {
          query.type = VideoGenerationType.IMAGE_TO_VIDEO;
        }
        const queryData: PaginationQuery<VideoGenerationQuery> = {
          query: query,
          pagination: {
            page,
            limit: 16,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          }
        };
        const response = await videoGenerationApi.videoGenerationPageList(queryData);
        setVideos(response.items);
        setPagination(response.pagination);
      }
      setCurrentPage(page);
    } catch (error) {
      console.error('加载视频失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedVideo(null);
      setSelectedLipsync(null);
      setCurrentPage(1);
      loadVideos(1);
    }
  }, [isOpen, search, activeTab]);

  const handleSelect = () => {
    if (selectedVideo) {
      onSelect(selectedVideo);
      onClose();
    } else if (selectedLipsync && selectedLipsync.resultVideoUrl) {
      onSelect({ resultVideoUrl: selectedLipsync.resultVideoUrl } as IVideoGeneration);
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-bg-surface rounded-lg w-[800px] h-[640px] flex flex-col border border-border-subtle shadow-2xl">
        <div className="flex items-center p-4 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-gray-50">{t('common_ui:selector.video.title')}</h2>
          <div className="flex bg-bg-elevated rounded-lg p-1 ml-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.video.tabAll')}
            </button>
            <button
              onClick={() => setActiveTab('text2video')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'text2video'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.video.tabText')}
            </button>
            <button
              onClick={() => setActiveTab('image2video')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'image2video'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.video.tabImage')}
            </button>
            <button
              onClick={() => setActiveTab('lipsync')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'lipsync'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-bg-surface'
                }`}
            >
              {t('common_ui:selector.video.tabLipsync')}
            </button>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common_ui:selector.video.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-80 pl-9 pr-4 py-1.5 border border-border-subtle rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-bg-form text-gray-50 placeholder-gray-500 text-sm"
              />
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (activeTab === 'lipsync' ? lipsyncVideos : videos).length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <div className="text-6xl mb-4">🎬</div>
              <p>{t('common_ui:selector.video.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(activeTab === 'lipsync' ? lipsyncVideos : videos).map((video) => (
                <div
                  key={video.id}
                  onClick={() => {
                    if (activeTab === 'lipsync') {
                      setSelectedLipsync(video as IVideoLipsync);
                      setSelectedVideo(null);
                    } else {
                      setSelectedVideo(video as IVideoGeneration);
                      setSelectedLipsync(null);
                    }
                  }}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${(activeTab === 'lipsync' ? selectedLipsync?.id : selectedVideo?.id) === video.id
                    ? 'border-blue-500 ring-2 ring-blue-400'
                    : 'border-border-subtle hover:border-border-default'
                    }`}
                >
                  <div className="aspect-video">
                    {video.resultVideoUrl ? (
                      <video
                        src={video.resultVideoUrl}
                        className="w-full h-full object-cover"
                        muted
                        disablePictureInPicture
                        onMouseEnter={(e) => e.currentTarget.play()}
                        onMouseLeave={(e) => e.currentTarget.pause()}
                      />
                    ) : (
                      <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
                        <VideoCameraIcon className="h-8 w-8 text-gray-500" />
                      </div>
                    )}
                  </div>

                  {(activeTab === 'lipsync' ? selectedLipsync?.id : selectedVideo?.id) === video.id && (
                    <div className="absolute top-2 left-2">
                      <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">✓</div>
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <div className="text-white text-xs">
                      {activeTab !== 'lipsync' && 'aspectRatio' in video && (
                        <div className="text-gray-300 text-xs">
                          {video.aspectRatio} · {video.duration}s
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="flex-1">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={loadVideos}
              showInfo={false}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedVideo && !selectedLipsync}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:selector.video.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}